import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Button,
  Stack,
  Typography,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Tooltip,
  styled,
  TextField,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ImageList,
  ImageListItem,
  Pagination,
  InputAdornment,
  IconButton,
  Chip
} from '@mui/material';
import {
  TextFields as TextIcon,
  Image as ImageIcon,
  PhotoLibrary as PhotosIcon,
  Dashboard as ElementsIcon,
  Wallpaper as BackgroundIcon,
  Wallpaper as WallpaperIcon,
  Palette,
  Layers as LayersIcon,
  Upload as UploadIcon,
  AutoFixHigh,
  Star as StarIcon,
  FontSize as FontSizeIcon,
  Title as HeadingIcon,
  FormatAlignLeft as ParagraphIcon,
  Category as ShapesIcon,
  EmojiEmotions as EmojiIcon,
  Category as CategoryIcon,
  Person as PersonIcon,
  Remove as LineIcon,
  ArrowForward as ArrowIcon,
  ChangeHistory as PolygonIcon,
  PanoramaFishEye as EllipseIcon,
  PieChart as WedgeIcon,
  Timeline as ArcIcon,
  RadioButtonUnchecked as RingIcon,
  Stop as RectangleIcon,
  Circle as CircleIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  DragIndicator as DragHandleIcon,
  AutoAwesome as GenerateIcon,
  Add,
  Search as SearchIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CropOriginalIcon from '@mui/icons-material/CropOriginal';
import { HexColorPicker } from 'react-colorful';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import imageService from '../../services/imageService';
import { toast } from 'react-hot-toast';
import { fabric } from 'fabric';
import axios from 'axios';

// Styled components
const SidebarContainer = styled(Paper)(({ theme }) => ({
  width: 80,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 0,
  borderRight: 'none',
  overflow: 'hidden',
  backgroundColor: '#ffffff',
  boxShadow: 'none',
  borderRight: '1px solid rgba(0, 0, 0, 0.08)'
}));

const SidebarItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active'
})(({ theme, active }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1.8, 0),
  cursor: 'pointer',
  borderLeft: active ? '3px solid #4890fe' : '3px solid transparent',
  backgroundColor: active ? 'rgba(72, 144, 254, 0.08)' : 'transparent',
  '&:hover': {
    backgroundColor: active ? 'rgba(72, 144, 254, 0.12)' : 'rgba(0, 0, 0, 0.03)',
  },
}));

const SidebarIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active'
})(({ theme, active }) => ({
  color: active ? '#4890fe' : '#64748b',
  marginBottom: theme.spacing(0.5),
}));

const SidebarLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'active'
})(({ theme, active }) => ({
  fontSize: '0.7rem',
  fontWeight: active ? 600 : 500,
  color: active ? '#4890fe' : '#64748b',
  textAlign: 'center',
}));

const ModernButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 2),
  justifyContent: 'flex-start',
  textTransform: 'none',
  fontWeight: 500,
  color: theme.palette.text.primary,
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
  '& .MuiButton-startIcon': {
    marginRight: theme.spacing(1.5),
  },
}));

const ElementButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: theme.spacing(1),
  minWidth: 0,
  width: '100%',
  height: 80,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(0.5),
  textTransform: 'none',
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: 'rgba(72, 144, 254, 0.04)',
    borderColor: '#4890fe',
  },
}));

const GroupedElementCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  border: '1px solid rgba(0, 0, 0, 0.08)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  backgroundColor: '#ffffff',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  '&:hover': {
    borderColor: '#4890fe',
    boxShadow: '0 4px 16px rgba(72, 144, 254, 0.1)',
    transform: 'translateY(-1px)',
  },
}));

const PreviewContainer = styled(Box)(({ theme }) => ({
  height: 120,
  backgroundColor: '#f8fafc',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  overflow: 'hidden',
  borderRadius: '12px 12px 0 0',
}));

const ElementTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.9rem',
  color: '#1e293b',
  marginBottom: theme.spacing(0.5),
}));

const ElementDescription = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: '#64748b',
  lineHeight: 1.3,
}));

// Constants
const SHAPES = [
  { id: 'rectangle', name: 'Rectangle', icon: <RectangleIcon /> },
  { id: 'circle', name: 'Circle', icon: <CircleIcon /> },
  { id: 'line', name: 'Line', icon: <LineIcon /> },
  { id: 'arrow', name: 'Arrow', icon: <ArrowIcon /> },
  { id: 'star', name: 'Star', icon: <StarIcon /> },
  { id: 'polygon', name: 'Polygon', icon: <PolygonIcon /> },
  { id: 'ellipse', name: 'Ellipse', icon: <EllipseIcon /> },
  { id: 'wedge', name: 'Wedge', icon: <WedgeIcon /> },
  { id: 'arc', name: 'Arc', icon: <ArcIcon /> },
  { id: 'ring', name: 'Ring', icon: <RingIcon /> }
];

const EMOJI_CATEGORIES = [
  {
    name: 'Smileys & People',
    emojis: ['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '☺️', '😚', '😙', '🥲', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥', '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '🥵', '🥶', '🥴', '😵', '🤯', '🤠', '🥳', '🥸', '😎', '🤓', '🧐', '😕', '😟', '🙁', '☹️', '😮', '😯', '😲', '😳', '🥺', '😦', '😧', '😨', '😰', '😥', '😢', '😭', '😱', '😖', '😣', '😞', '😓', '😩', '😫', '🥱', '😤', '😡', '😠', '🤬', '😈', '👿', '💀', '☠️']
  },
  {
    name: 'Objects',
    emojis: ['⌚', '📱', '📲', '💻', '⌨️', '🖥️', '🖨️', '🖱️', '🖲️', '🕹️', '🗜️', '💽', '💾', '💿', '📀', '📼', '📷', '📸', '📹', '🎥', '📽️', '🎞️', '📞', '☎️', '📟', '📠', '📺', '📻', '🎙️', '🎚️', '🎛️', '🧭', '⏱️', '⏲️', '⏰', '🕰️', '⌛', '⏳', '📡', '🔋', '🔌', '💡', '🔦', '🕯️', '🪔', '🧯', '🛢️', '💸', '💵', '💴', '💶', '💷', '🪙', '💰', '💳', '💎', '⚖️', '🪜', '🧰', '🔧', '🔨', '⚒️', '🛠️', '⛏️', '🪓', '🪚', '🔩', '⚙️', '🪤', '🧱', '⛓️', '🧲', '🔫', '💣', '🧨', '🪓', '🔪', '🗡️', '⚔️', '🛡️', '🚬', '⚰️', '🪦', '⚱️', '🏺', '🔮', '📿', '🧿', '💈', '⚗️', '🔭', '🔬', '🕳️', '🩹', '🩺', '💊', '💉', '🩸', '🧬', '🦠', '🧫', '🧪', '🌡️', '🧹', '🪠', '🧺', '🧻', '🚽', '🚰', '🚿', '🛁', '🛀', '🧼', '🪥', '🪒', '🧽', '🪣', '🧴', '🛎️', '🔑', '🗝️', '🚪', '🪑', '🛋️', '🛏️', '🛌', '🧸', '🪆', '🖼️', '🪞', '🪟', '🛍️', '🛒', '🎁', '🎈', '🎏', '🎀', '🪄', '🪅', '🎊', '🎉', '🎎', '🏮', '🎐', '🧧', '✉️', '📩', '📨', '📧', '💌', '📥', '📤', '📦', '🏷️', '🪧', '📪', '📫', '📬', '📭', '📮', '📯', '📜', '📃', '📄', '📑', '🧾', '📊', '📈', '📉', '🗒️', '🗓️', '📆', '📅', '🗑️', '📇', '🗃️', '🗳️', '🗄️', '📋', '📁', '📂', '🗂️', '🗞️', '📰', '📓', '📔', '📒', '📕', '📗', '📘', '📙', '📚', '📖', '🔖', '🧷', '🔗', '📎', '🖇️', '📐', '📏', '🧮', '📌', '📍', '✂️', '🖊️', '🖋️', '✒️', '🖌️', '🖍️', '📝', '✏️', '🔍', '🔎', '🔏', '🔐', '🔒', '🔓']
  },
  {
    name: 'Nature',
    emojis: ['🌱', '🌿', '🍀', '🍃', '🍂', '🍁', '🌾', '🌲', '🌳', '🌴', '🌵', '🌶️', '🍄', '🌰', '🌻', '🌺', '🌸', '🌼', '🌷', '🥀', '🌹', '🏵️', '💮', '🌋', '⛰️', '🏔️', '🗻', '🏕️', '🏖️', '🏜️', '🏝️', '🏞️', '🌅', '🌄', '🌠', '🎇', '🎆', '🌇', '🌆', '🏙️', '🌃', '🌌', '🌉', '🌁', '⭐', '🌟', '💫', '✨', '☄️', '🌙', '🌛', '🌜', '🌚', '🌕', '🌖', '🌗', '🌘', '🌑', '🌒', '🌓', '🌔', '🌍', '🌎', '🌏', '🪐', '💧', '🌊', '☔', '⛈️', '🌩️', '🌨️', '❄️', '☃️', '⛄', '🌬️', '💨', '🌪️', '🌫️', '🌈', '☀️', '🌤️', '⛅', '🌥️', '☁️', '🌦️', '⛈️', '🌧️', '⚡', '🔥', '💥', '❄️', '🌨️', '☃️', '⛄']
  },
  {
    name: 'Food & Drink',
    emojis: ['🍎', '🍏', '🍐', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🫐', '🍈', '🍒', '🍑', '🥭', '🍍', '🥥', '🥝', '🍅', '🍆', '🥑', '🥦', '🥬', '🥒', '🌶️', '🫑', '🌽', '🥕', '🫒', '🧄', '🧅', '🥔', '🍠', '🥐', '🥯', '🍞', '🥖', '🥨', '🧀', '🥚', '🍳', '🧈', '🥞', '🧇', '🥓', '🥩', '🍗', '🍖', '🦴', '🌭', '🍔', '🍟', '🍕', '🫓', '🥪', '🥙', '🧆', '🌮', '🌯', '🥗', '🥫', '🍝', '🍜', '🍲', '🍛', '🍣', '🍱', '🥟', '🦪', '🍤', '🍙', '🍚', '🍘', '🍥', '🥠', '🥮', '🍢', '🍡', '🍧', '🍨', '🍦', '🥧', '🧁', '🍰', '🎂', '🍮', '🍭', '🍬', '🍫', '🍿', '🍩', '🍪', '🌰', '🥜', '🍯', '🥛', '🍼', '☕', '🫖', '🍵', '🧃', '🥤', '🧋', '🍶', '🍺', '🍻', '🥂', '🍷', '🥃', '🍸', '🍹', '🧉', '🍾']
  }
];

const ELEMENT_TABS = [
  { id: 'basic', label: 'Basic', icon: <CategoryIcon /> },
  { id: 'grouped', label: 'Grouped', icon: <PersonIcon /> },
  { id: 'shapes', label: 'Shapes', icon: <ShapesIcon /> },
  { id: 'emojis', label: 'Emojis', icon: <EmojiIcon /> }
];

const GROUPED_ELEMENTS = [
  {
    id: 'profile-header',
    title: 'Profile Header',
    description: 'Complete profile section with picture and name',
    preview: (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, transform: 'scale(0.7)' }}>
        <Box sx={{ 
          width: 32, 
          height: 32, 
          borderRadius: '50%', 
          backgroundColor: '#e0e0e0',
          border: '2px solid white'
        }} />
        <Box>
          <Typography sx={{ fontSize: '10px', fontWeight: 'bold', color: '#000' }}>
            Abdul Subhan Fahad
          </Typography>
          <Typography sx={{ fontSize: '8px', color: '#666' }}>
            @abdulsubhanf
          </Typography>
        </Box>
      </Box>
    )
  },
  {
    id: 'swipe-left',
    title: 'Swipe Left Indicator',
    description: 'Social media swipe navigation hint',
    preview: (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 0.5,
        backgroundColor: 'rgba(0,0,0,0.7)',
        borderRadius: 2,
        px: 1.5,
        py: 0.5,
        transform: 'scale(0.8)'
      }}>
        <Typography sx={{ color: 'white', fontSize: '12px' }}>←</Typography>
        <Typography sx={{ color: 'white', fontSize: '8px', fontWeight: 500 }}>
          Swipe Left
        </Typography>
      </Box>
    )
  },
  {
    id: 'call-to-action',
    title: 'Call to Action',
    description: 'Engaging button for user interaction',
    preview: (
      <Box sx={{ 
        backgroundColor: '#4890fe',
        borderRadius: 3,
        px: 2,
        py: 1,
        transform: 'scale(0.7)'
      }}>
        <Typography sx={{ color: 'white', fontSize: '10px', fontWeight: 'bold' }}>
          Follow for more tips!
        </Typography>
      </Box>
    )
  },
  {
    id: 'social-proof',
    title: 'Social Proof',
    description: 'Likes and engagement metrics',
    preview: (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, transform: 'scale(0.8)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: '12px' }}>❤️</Typography>
          <Typography sx={{ fontSize: '8px', color: '#333' }}>1.2K</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: '12px' }}>💬</Typography>
          <Typography sx={{ fontSize: '8px', color: '#333' }}>86</Typography>
        </Box>
      </Box>
    )
  },
  {
    id: 'quote-attribution',
    title: 'Quote with Attribution',
    description: 'Styled quote block with author',
    preview: (
      <Box sx={{ 
        backgroundColor: 'rgba(255,255,255,0.9)',
        border: '1px solid #e0e0e0',
        borderRadius: 1.5,
        p: 1,
        textAlign: 'center',
        transform: 'scale(0.6)'
      }}>
        <Typography sx={{ fontSize: '8px', fontStyle: 'italic', color: '#333', mb: 0.5 }}>
          "The best time to plant a tree..."
        </Typography>
        <Typography sx={{ fontSize: '6px', color: '#666', fontWeight: 600 }}>
          - Chinese Proverb
        </Typography>
      </Box>
    )
  },
  {
    id: 'badge-label',
    title: 'Badge/Label',
    description: 'Notification or status badge',
    preview: (
      <Box sx={{ 
        backgroundColor: '#ff6b6b',
        borderRadius: 1.5,
        px: 1.5,
        py: 0.5,
        transform: 'scale(0.8)'
      }}>
        <Typography sx={{ color: 'white', fontSize: '8px', fontWeight: 'bold' }}>
          NEW
        </Typography>
      </Box>
    )
  },
  // New grouped elements
  {
    id: 'testimonial-card',
    title: 'Testimonial Card',
    description: 'Customer review with rating stars',
    preview: (
      <Box sx={{ 
        backgroundColor: 'white',
        border: '1px solid #e0e0e0',
        borderRadius: 1.5,
        p: 1,
        transform: 'scale(0.6)',
        width: 120
      }}>
        <Box sx={{ display: 'flex', mb: 0.5 }}>
          <Typography sx={{ fontSize: '8px', color: '#ffc107' }}>★★★★★</Typography>
        </Box>
        <Typography sx={{ fontSize: '7px', color: '#333', mb: 0.5 }}>
          "This product changed my life! Highly recommend to everyone."
        </Typography>
        <Typography sx={{ fontSize: '6px', color: '#666', fontWeight: 600 }}>
          - Sarah Johnson
        </Typography>
      </Box>
    )
  },
  {
    id: 'step-indicator',
    title: 'Step Indicator',
    description: 'Numbered step for tutorials or guides',
    preview: (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, transform: 'scale(0.8)' }}>
        <Box sx={{ 
          width: 20, 
          height: 20, 
          borderRadius: '50%', 
          backgroundColor: '#8b5cf6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Typography sx={{ fontSize: '10px', color: 'white', fontWeight: 'bold' }}>
            1
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '8px', color: '#333', fontWeight: 500 }}>
          First step description
        </Typography>
      </Box>
    )
  },
  {
    id: 'stat-card',
    title: 'Stat Card',
    description: 'Key metric with label and icon',
    preview: (
      <Box sx={{ 
        backgroundColor: 'white',
        border: '1px solid #e0e0e0',
        borderRadius: 1.5,
        p: 1,
        width: 80,
        transform: 'scale(0.7)'
      }}>
        <Typography sx={{ fontSize: '12px', color: '#10b981', fontWeight: 'bold' }}>
          +27%
        </Typography>
        <Typography sx={{ fontSize: '6px', color: '#666' }}>
          Engagement Rate
        </Typography>
      </Box>
    )
  },
  {
    id: 'feature-list',
    title: 'Feature List',
    description: 'Bulleted list with checkmarks',
    preview: (
      <Box sx={{ transform: 'scale(0.7)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <Typography sx={{ fontSize: '10px', color: '#10b981' }}>✓</Typography>
          <Typography sx={{ fontSize: '8px', color: '#333' }}>Feature one</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <Typography sx={{ fontSize: '10px', color: '#10b981' }}>✓</Typography>
          <Typography sx={{ fontSize: '8px', color: '#333' }}>Feature two</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: '10px', color: '#10b981' }}>✓</Typography>
          <Typography sx={{ fontSize: '8px', color: '#333' }}>Feature three</Typography>
        </Box>
      </Box>
    )
  },
  {
    id: 'author-bio',
    title: 'Author Bio',
    description: 'Short author description with credentials',
    preview: (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1, 
        transform: 'scale(0.7)',
        p: 0.5,
        border: '1px solid #e0e0e0',
        borderRadius: 1
      }}>
        <Box sx={{ 
          width: 24, 
          height: 24, 
          borderRadius: '50%', 
          backgroundColor: '#e0e0e0'
        }} />
        <Box>
          <Typography sx={{ fontSize: '8px', fontWeight: 'bold', color: '#000' }}>
            Jane Smith
          </Typography>
          <Typography sx={{ fontSize: '6px', color: '#666' }}>
            Marketing Director | Speaker
          </Typography>
        </Box>
      </Box>
    )
  },
  // LinkedIn-specific elements
  {
    id: 'linkedin-header',
    title: 'LinkedIn Header',
    description: 'Professional header with logo and title',
    preview: (
      <Box sx={{ 
        width: '100%',
        p: 1,
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        transform: 'scale(0.7)',
        bgcolor: 'white'
      }}>
        <Box sx={{ 
          width: 24, 
          height: 24, 
          borderRadius: '4px', 
          bgcolor: '#0a66c2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '14px',
          fontWeight: 'bold'
        }}>in</Box>
        <Box>
          <Typography sx={{ fontSize: '10px', fontWeight: 'bold', color: '#000' }}>
            LinkedIn Insights
          </Typography>
          <Typography sx={{ fontSize: '6px', color: '#666' }}>
            Professional Growth Series
          </Typography>
        </Box>
      </Box>
    )
  },
  {
    id: 'numbered-list',
    title: 'Numbered List',
    description: 'Organized points with numbers',
    preview: (
      <Box sx={{ transform: 'scale(0.7)' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
          <Box sx={{ 
            width: 16, 
            height: 16, 
            borderRadius: '50%', 
            bgcolor: '#0a66c2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '10px',
            fontWeight: 'bold'
          }}>1</Box>
          <Typography sx={{ fontSize: '8px', color: '#333' }}>First point about professional growth</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
          <Box sx={{ 
            width: 16, 
            height: 16, 
            borderRadius: '50%', 
            bgcolor: '#0a66c2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '10px',
            fontWeight: 'bold'
          }}>2</Box>
          <Typography sx={{ fontSize: '8px', color: '#333' }}>Second point about networking</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ 
            width: 16, 
            height: 16, 
            borderRadius: '50%', 
            bgcolor: '#0a66c2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '10px',
            fontWeight: 'bold'
          }}>3</Box>
          <Typography sx={{ fontSize: '8px', color: '#333' }}>Third point about industry trends</Typography>
        </Box>
      </Box>
    )
  },
  {
    id: 'comparison-box',
    title: 'Comparison Box',
    description: 'Side-by-side comparison for LinkedIn',
    preview: (
      <Box sx={{ 
        display: 'flex',
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        overflow: 'hidden',
        transform: 'scale(0.6)',
        width: 140
      }}>
        <Box sx={{ 
          flex: 1, 
          p: 0.5, 
          bgcolor: '#f9fafb', 
          borderRight: '1px solid #e0e0e0',
          textAlign: 'center'
        }}>
          <Typography sx={{ fontSize: '7px', fontWeight: 'bold', color: '#dc2626', mb: 0.5 }}>
            DON'T
          </Typography>
          <Typography sx={{ fontSize: '6px', color: '#666' }}>
            Generic content
          </Typography>
        </Box>
        <Box sx={{ 
          flex: 1, 
          p: 0.5, 
          bgcolor: '#f0f9ff',
          textAlign: 'center'
        }}>
          <Typography sx={{ fontSize: '7px', fontWeight: 'bold', color: '#16a34a', mb: 0.5 }}>
            DO
          </Typography>
          <Typography sx={{ fontSize: '6px', color: '#666' }}>
            Targeted insights
          </Typography>
        </Box>
      </Box>
    )
  },
  {
    id: 'key-takeaway',
    title: 'Key Takeaway',
    description: 'Highlighted important point',
    preview: (
      <Box sx={{ 
        p: 1,
        border: '2px solid #0a66c2',
        borderRadius: 1,
        bgcolor: 'rgba(10, 102, 194, 0.05)',
        transform: 'scale(0.7)',
        width: 120,
        textAlign: 'center'
      }}>
        <Typography sx={{ fontSize: '8px', color: '#0a66c2', fontWeight: 'bold', mb: 0.5 }}>
          KEY TAKEAWAY
        </Typography>
        <Typography sx={{ fontSize: '7px', color: '#333' }}>
          Focus on building authentic professional relationships
        </Typography>
      </Box>
    )
  },
  {
    id: 'progress-bar',
    title: 'Progress Bar',
    description: 'Visual progress indicator',
    preview: (
      <Box sx={{ transform: 'scale(0.7)', width: 120 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography sx={{ fontSize: '7px', fontWeight: 'bold', color: '#333' }}>
            Growth Progress
          </Typography>
          <Typography sx={{ fontSize: '7px', fontWeight: 'bold', color: '#0a66c2' }}>
            75%
          </Typography>
        </Box>
        <Box sx={{ 
          width: '100%', 
          height: 6, 
          bgcolor: '#e5e7eb',
          borderRadius: 3,
          overflow: 'hidden'
        }}>
          <Box sx={{ 
            width: '75%', 
            height: '100%', 
            bgcolor: '#0a66c2',
            borderRadius: 3
          }} />
        </Box>
      </Box>
    )
  }
];

const GRADIENT_PRESETS = [
  {
    name: 'Ocean Blue',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    colors: ['#667eea', '#764ba2']
  },
  {
    name: 'Sunset',
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    colors: ['#f093fb', '#f5576c']
  },
  {
    name: 'Forest',
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    colors: ['#4facfe', '#00f2fe']
  },
  {
    name: 'Purple Rain',
    gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    colors: ['#a8edea', '#fed6e3']
  },
  {
    name: 'Fire',
    gradient: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    colors: ['#ff9a9e', '#fecfef']
  },
  {
    name: 'Cool Sky',
    gradient: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
    colors: ['#a1c4fd', '#c2e9fb']
  },
  {
    name: 'Warm Flame',
    gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    colors: ['#ffecd2', '#fcb69f']
  },
  {
    name: 'Night Sky',
    gradient: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
    colors: ['#2c3e50', '#3498db']
  }
];

const BRAND_ACCENTS = [
  {
    name: 'LinkedIn Blue',
    primary: '#0077b5',
    secondary: '#004182',
    accent: '#00a0dc',
    light: '#e6f3ff'
  },
  {
    name: 'Tech Green',
    primary: '#10b981',
    secondary: '#047857',
    accent: '#34d399',
    light: '#ecfdf5'
  },
  {
    name: 'Creative Purple',
    primary: '#8b5cf6',
    secondary: '#5b21b6',
    accent: '#a78bfa',
    light: '#f3f4f6'
  },
  {
    name: 'Professional Navy',
    primary: '#1e40af',
    secondary: '#1e3a8a',
    accent: '#3b82f6',
    light: '#eff6ff'
  },
  {
    name: 'Modern Orange',
    primary: '#f97316',
    secondary: '#ea580c',
    accent: '#fb923c',
    light: '#fff7ed'
  },
  {
    name: 'Elegant Pink',
    primary: '#ec4899',
    secondary: '#be185d',
    accent: '#f472b6',
    light: '#fdf2f8'
  }
];

const presetColors = [
  '#000000', '#FFFFFF', '#3B82F6', '#EF4444', '#10B981', '#F59E0B',
  '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
  '#1F2937', '#6B7280', '#9CA3AF', '#D1D5DB', '#FEE2E2', '#DBEAFE'
];

const IMAGE_STYLES = [
  { value: 'realistic', label: 'Realistic', prompt: 'in realistic style, highly detailed' },
  { value: 'cartoon', label: 'Cartoon', prompt: 'in cartoon style, animated, vibrant' },
  { value: 'watercolor', label: 'Watercolor', prompt: 'in watercolor style, artistic, painted' },
  { value: 'digital-art', label: 'Digital Art', prompt: 'in digital art style, modern, sharp' },
  { value: 'anime', label: 'Anime', prompt: 'in anime style, japanese animation' },
  { value: '3d', label: '3D Render', prompt: 'in 3D rendered style, octane render' },
  { value: 'minimalist', label: 'Minimalist', prompt: 'in minimalist style, simple, clean' },
  { value: 'pop-art', label: 'Pop Art', prompt: 'in pop art style, bold colors' }
];

// Add pattern background presets
const PATTERN_PRESETS = [
  {
    name: 'Dots',
    pattern: 'dots',
    background: '#ffffff',
    foreground: '#e0e0e0'
  },
  {
    name: 'Grid',
    pattern: 'grid',
    background: '#ffffff',
    foreground: '#e0e0e0'
  },
  {
    name: 'Stripes',
    pattern: 'stripes',
    background: '#ffffff',
    foreground: '#e0e0e0'
  },
  {
    name: 'Diagonal',
    pattern: 'diagonal',
    background: '#ffffff',
    foreground: '#e0e0e0'
  },
  {
    name: 'Checkerboard',
    pattern: 'checkerboard',
    background: '#ffffff',
    foreground: '#f0f0f0'
  },
  {
    name: 'Zigzag',
    pattern: 'zigzag',
    background: '#ffffff',
    foreground: '#e0e0e0'
  }
];

const CarouselLeftSidebar = ({
  // Sidebar state
  activeSection,
  handleSectionClick,
  
  // Element actions
  addHeading,
  addParagraph,
  addImage,
  addProfilePlaceholder,
  addLogoPlaceholder,
  addSquareProfilePlaceholder,
  addGroupedElement,
  addShape,
  addEmoji,
  
  // Element tab state
  activeElementTab,
  setActiveElementTab,
  
  // Photos actions
  setShowPexels,
  removeBackground,
  removeBackgroundAI,
  isRemovingBackground,
  
  // Background actions
  activeBackgroundTab,
  setActiveBackgroundTab,
  background,
  setBackground,
  fabricCanvasRef,
  applyGradient,
  applyPattern,
  
  // Branding actions
  selectedBrandAccent,
  applyBrandAccent,
  
  // Layers
  canvasObjects,
  setSelectedObject,
  updateObjectsList,
  toggleObjectLock,
  toggleObjectVisibility,
  
  // Page management
  currentPageIndex,
  pages,
  
  // Snackbar
  setSnackbar,
  canvasSize
}) => {
  const sidebarSections = [
    { id: 'elements', label: 'Elements', icon: <ElementsIcon /> },
    { id: 'text', label: 'Text', icon: <TextIcon /> },
    { id: 'photos', label: 'Photos', icon: <PhotosIcon /> },
    { id: 'ai-image', label: 'AI Image', icon: <GenerateIcon /> },
    { id: 'background', label: 'Background', icon: <BackgroundIcon /> },
    { id: 'branding', label: 'Branding', icon: <Palette /> },
    { id: 'layers', label: 'Layers', icon: <LayersIcon /> }
  ];

  // AI Image Generator state
  const [prompt, setPrompt] = useState('');
  const [generatedImage, setGeneratedImage] = useState(null);
  const [imageSize, setImageSize] = useState('square_hd');
  const [loading, setLoading] = useState(false);
  const [imageStyle, setImageStyle] = useState('');
  
  // Pexels API key
  const PEXELS_API_KEY = process.env.REACT_APP_PEXELS_API_KEY;
  
  // Photos search state
  const [photosSearchQuery, setPhotosSearchQuery] = useState('people photography');
  const [photosImages, setPhotosImages] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [photosError, setPhotosError] = useState('');
  const [photosPage, setPhotosPage] = useState(1);
  const [photosTotalPages, setPhotosTotalPages] = useState(1);
  
  // Background search state
  const [backgroundSearchQuery, setBackgroundSearchQuery] = useState('abstract background');
  const [backgroundImages, setBackgroundImages] = useState([]);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [backgroundError, setBackgroundError] = useState('');
  const [backgroundPage, setBackgroundPage] = useState(1);
  const [backgroundTotalPages, setBackgroundTotalPages] = useState(1);
  
  // Background application mode
  const [backgroundMode, setBackgroundMode] = useState('all'); // 'all' or 'perSlide'
  const [selectedSlideForBackground, setSelectedSlideForBackground] = useState(0);
  
  // Pattern customization state
  const [selectedPatternType, setSelectedPatternType] = useState('dots');
  const [patternBgColor, setPatternBgColor] = useState('#ffffff');
  const [patternFgColor, setPatternFgColor] = useState('#e0e0e0');
  
  // Pexels categories for backgrounds
  const pexelsCategories = [
    'Abstract', 'Gradient', 'Texture', 'Pattern', 'Minimal',
    'Geometric', 'Nature', 'Bokeh', 'Marble', 'Colorful'
  ];

  // Function to apply gradient to a specific slide
  const applyGradientToSlide = (gradient, slideIndex) => {
    const canvas = fabricCanvasRef.current;
    const slideWidth = 1080;
    // Get the actual canvas height from the canvasSize prop
    const slideHeight = canvasSize?.height || 1350; // Default to 1350 if canvasSize is not available
    const pageOffset = slideIndex * slideWidth;
    
    // Remove any existing background objects on this slide
    const objects = canvas.getObjects();
    const existingBgs = objects.filter(obj => 
      obj.objectType === 'background' && 
      (obj.pageId === slideIndex || obj.pageId === slideIndex.toString())
    );
    
    existingBgs.forEach(bg => canvas.remove(bg));
    
    // Create a gradient
    const fabricGradient = new fabric.Gradient({
      type: 'linear',
      coords: {
        x1: 0,
        y1: 0,
        x2: slideWidth,
        y2: slideHeight
      },
      colorStops: [
        { offset: 0, color: String(gradient.colors[0]) },
        { offset: 1, color: String(gradient.colors[1]) }
      ]
    });
    
    // Create a new background rectangle with gradient
    const rect = new fabric.Rect({
      left: pageOffset,
      top: 0,
      width: slideWidth,
      height: slideHeight,
      fill: fabricGradient,
      selectable: true,
      hasControls: true,
      hasBorders: true,
      lockMovementX: false,
      lockMovementY: false,
      lockRotation: false,
      lockScalingX: false,
      lockScalingY: false,
      evented: true,
      objectType: 'background',
      pageId: String(slideIndex), // Convert to string to ensure consistent type
      name: `Background Gradient (Slide ${slideIndex + 1})`
    });
    
    // Add the new background and send it to the back
    canvas.add(rect);
    rect.sendToBack();
    canvas.renderAll();
    
    // Update objects list if needed
    if (updateObjectsList) {
      updateObjectsList();
    }
  };

  // Function to apply gradient to all slides
  const applyGradientToAllSlides = (gradient) => {
    const totalSlides = pages?.length || 1;
    
    // Apply gradient to each slide
    for (let i = 0; i < totalSlides; i++) {
      applyGradientToSlide(gradient, i);
    }
    
    setSnackbar({
      open: true,
      message: `Gradient applied to all ${totalSlides} slides`,
      severity: 'success'
    });
  };

  const [reorderObjects, setReorderObjects] = useState(false);

  const handleDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const sourcePageId = result.source.droppableId;
    const destinationPageId = result.destination.droppableId;
    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourcePageId === destinationPageId) {
      const updatedObjects = [...canvasObjects];
      const [movedObject] = updatedObjects.splice(sourceIndex, 1);
      updatedObjects.splice(destinationIndex, 0, movedObject);
      updateObjectsList(updatedObjects);
    } else {
      const sourcePageObjects = canvasObjects.filter(obj => obj.pageId === sourcePageId);
      const destinationPageObjects = canvasObjects.filter(obj => obj.pageId === destinationPageId);

      const [movedObject] = sourcePageObjects.splice(sourceIndex, 1);
      destinationPageObjects.splice(destinationIndex, 0, movedObject);

      updateObjectsList(canvasObjects); // Re-render all objects to update their pageId
    }
  };

  const handleGenerateImage = async () => {
    if (!prompt) {
      toast.error('Please enter a prompt');
      return;
    }

    try {
      setLoading(true);
      setGeneratedImage(null);

      // Use imageService with InstaImage API
      const response = await imageService.generateImage(prompt, imageSize);

      if (response && response.url) {
        setGeneratedImage(response.url);
        toast.success('Image generated successfully with InstaImage AI');
      } else {
        throw new Error('No image URL received');
      }
    } catch (error) {
      console.error('Error Details:', error);
      if (error?.response?.status === 402 || error?.message?.includes('Insufficient')) {
        toast.error('Insufficient credits. Please upgrade your plan.');
        return;
      }
      toast.error('Failed to generate image. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddGeneratedImage = async () => {
    if (!generatedImage) {
      toast.error('No image generated yet');
      return;
    }

    try {
      setLoading(true);
      
      // Use fabric.js to add the image directly to the canvas
      if (fabricCanvasRef.current) {
        fabric.Image.fromURL(generatedImage, (img) => {
          const canvas = fabricCanvasRef.current;
          
          // Get the current page offset
          const pageOffset = currentPageIndex * 1080; // Assuming page width is 1080
          const slideWidth = 1080;
          const slideHeight = 1080;
          
          // Calculate scale to ensure image fits properly on the canvas
          // Always scale to fill at least the full height or width of the slide
          const scaleX = slideWidth / img.width;
          const scaleY = slideHeight / img.height;
          
          // Use the larger scale to ensure image covers the canvas area
          const scale = Math.max(scaleX, scaleY);
          
          img.set({
            left: pageOffset + (slideWidth / 2), // Center in current page
            top: slideHeight / 2, // Center vertically
            originX: 'center',
            originY: 'center',
            scaleX: scale,
            scaleY: scale,
            id: 'ai_image_' + Date.now()
          });

          canvas.add(img);
          canvas.setActiveObject(img);
          canvas.renderAll();
          
          // Update objects list if needed
          if (updateObjectsList) {
            updateObjectsList();
          }
          
          setSnackbar({
            open: true,
            message: 'AI image added to canvas',
            severity: 'success'
          });
        });
      } else {
        throw new Error('Canvas not initialized');
      }
    } catch (error) {
      console.error('Error adding generated image:', error);
      toast.error('Failed to add image to canvas');
    } finally {
      setLoading(false);
    }
  };

  const handleStyleChange = (event) => {
    const selectedStyle = event.target.value;
    setImageStyle(selectedStyle);
    
    let cleanPrompt = prompt;
    IMAGE_STYLES.forEach(style => {
      cleanPrompt = cleanPrompt.replace(style.prompt, '').trim();
    });

    if (selectedStyle) {
      const stylePrompt = IMAGE_STYLES.find(style => style.value === selectedStyle)?.prompt || '';
      setPrompt(`${cleanPrompt} ${stylePrompt}`.trim());
    } else {
      setPrompt(cleanPrompt);
    }
  };
  
  // Photos search functions
  const searchPhotos = async (searchQuery = photosSearchQuery, pageNum = 1) => {
    if (!searchQuery) searchQuery = 'people photography';
    
    setPhotosLoading(true);
    setPhotosError('');
    
    try {
      const response = await axios.get(`https://api.pexels.com/v1/search`, {
        params: { 
          query: searchQuery, 
          per_page: 8, 
          page: pageNum 
        },
        headers: { 
          Authorization: PEXELS_API_KEY 
        }
      });
      
      setPhotosImages(response.data.photos);
      setPhotosTotalPages(Math.ceil(response.data.total_results / 8));
    } catch (err) {
      console.error('Error fetching Pexels photos:', err);
      setPhotosError('Failed to fetch images. Please try again.');
    } finally {
      setPhotosLoading(false);
    }
  };

  const handlePhotosSearch = (e) => {
    e.preventDefault();
    setPhotosPage(1);
    searchPhotos(photosSearchQuery);
  };

  const handlePhotosCategoryClick = (category) => {
    setPhotosSearchQuery(category);
    setPhotosPage(1);
    searchPhotos(category);
  };

  const handlePhotosPageChange = (event, value) => {
    setPhotosPage(value);
    searchPhotos(photosSearchQuery, value);
  };
  
  // Background search functions
  const searchBackgrounds = async (searchQuery = backgroundSearchQuery, pageNum = 1) => {
    if (!searchQuery) searchQuery = 'abstract background';
    
    // For background tab, append "background" to the search query if it's not already there
    if (!searchQuery.toLowerCase().includes('background') && 
        !searchQuery.toLowerCase().includes('texture') && 
        !searchQuery.toLowerCase().includes('pattern')) {
      searchQuery = `${searchQuery} background`;
    }
    
    setBackgroundLoading(true);
    setBackgroundError('');
    
    try {
      const response = await axios.get(`https://api.pexels.com/v1/search`, {
        params: { 
          query: searchQuery, 
          per_page: 8, 
          page: pageNum 
        },
        headers: { 
          Authorization: PEXELS_API_KEY 
        }
      });
      
      setBackgroundImages(response.data.photos);
      setBackgroundTotalPages(Math.ceil(response.data.total_results / 8));
    } catch (err) {
      console.error('Error fetching background images:', err);
      setBackgroundError('Failed to fetch images. Please try again.');
    } finally {
      setBackgroundLoading(false);
    }
  };

  const handleBackgroundSearch = (e) => {
    e.preventDefault();
    setBackgroundPage(1);
    searchBackgrounds(backgroundSearchQuery);
  };

  const handleBackgroundCategoryClick = (category) => {
    setBackgroundSearchQuery(category);
    setBackgroundPage(1);
    searchBackgrounds(category);
  };

  const handleBackgroundPageChange = (event, value) => {
    setBackgroundPage(value);
    searchBackgrounds(backgroundSearchQuery, value);
  };
  
  // Load images when the sections are selected
  useEffect(() => {
    // Load photos when Photos section is selected
    if (activeSection === 'photos') {
      if (photosImages.length === 0) {
        searchPhotos('people photography');
      }
    }
    
    // Load backgrounds when Background section is selected
    if (activeSection === 'background' && activeBackgroundTab === 1) {
      if (backgroundImages.length === 0) {
        searchBackgrounds('abstract background');
      }
    }
  }, [activeSection, activeBackgroundTab]);
  
    // Handle selecting an image from Photos section
    const handleSelectPhotosImage = (image) => {
      if (fabricCanvasRef.current) {
        const canvas = fabricCanvasRef.current;
        
        // Add the image to the canvas at the center of the current slide
        const currentSlide = currentPageIndex || 0;
        const slideWidth = 1080;
        const slideHeight = canvasSize?.height || 1350;
        const pageOffset = currentSlide * slideWidth;
        
        fabric.Image.fromURL(image.src.large, (img) => {
          // Scale the image to a reasonable size (50% of slide width)
          const maxWidth = slideWidth * 0.5;
          const scale = maxWidth / img.width;
          
          img.set({
            left: pageOffset + slideWidth / 2,
            top: slideHeight / 2,
            scaleX: scale,
            scaleY: scale,
            originX: 'center',
            originY: 'center',
            selectable: true,
            hasControls: true,
            hasBorders: true,
            name: `Image from Pexels (${image.photographer})`,
            objectType: 'image',
            pageId: currentSlide.toString()
          });
          
          canvas.add(img);
          canvas.setActiveObject(img);
          canvas.renderAll();
          
          // Update objects list if needed
          if (updateObjectsList) {
            updateObjectsList();
          }
          
          setSnackbar({
            open: true,
            message: 'Image added to slide',
            severity: 'success'
          });
        }, { crossOrigin: 'anonymous' });
      }
    };
    
    // Handle selecting a background image
    const handleSelectBackgroundImage = (image) => {
      if (fabricCanvasRef.current) {
        const canvas = fabricCanvasRef.current;
        const slideWidth = 1080;
        // Get the actual canvas height from the canvasSize prop
        const slideHeight = canvasSize?.height || 1350; // Default to 1350 if canvasSize is not available
        
        // Function to add background to a specific slide
        const addBackgroundToSlide = (slideIndex) => {
          const pageOffset = slideIndex * slideWidth;
          
          fabric.Image.fromURL(image.src.large, (img) => {
            // Scale the image to cover the slide
            const scaleX = slideWidth / img.width;
            const scaleY = slideHeight / img.height;
            const scale = Math.max(scaleX, scaleY);
            
            img.set({
              left: pageOffset,
              top: 0,
              scaleX: scale,
              scaleY: scale,
              originX: 'left',
              originY: 'top',
              selectable: true,
              hasControls: true,
              hasBorders: true,
              lockMovementX: false,
              lockMovementY: false,
              lockRotation: false,
              lockScalingX: false,
              lockScalingY: false,
              evented: true,
              objectType: 'background',
              pageId: slideIndex.toString(), // Convert to string to ensure consistent type
              name: `Background Image (Slide ${slideIndex + 1})`
            });
            
            // Remove any existing background images on this slide
            const objects = canvas.getObjects();
            const existingBgs = objects.filter(obj => 
              obj.objectType === 'background' && 
              (obj.pageId === slideIndex || obj.pageId === slideIndex.toString())
            );
            
            existingBgs.forEach(bg => canvas.remove(bg));
            
            // Add the new background and send it to the back
            canvas.add(img);
            img.sendToBack();
          }, { crossOrigin: 'anonymous' });
        };
        
        // Apply background based on selected mode
        if (backgroundMode === 'all') {
          // Apply to all slides
          const totalSlides = pages?.length || 1;
          for (let i = 0; i < totalSlides; i++) {
            addBackgroundToSlide(i);
          }
          
          setSnackbar({
            open: true,
            message: `Background applied to all ${totalSlides} slides`,
            severity: 'success'
          });
        } else {
          // Apply to selected slide only
          addBackgroundToSlide(selectedSlideForBackground);
          
          setSnackbar({
            open: true,
            message: `Background applied to slide ${selectedSlideForBackground + 1}`,
            severity: 'success'
          });
        }
        
        // Render and update objects list
        canvas.renderAll();
        if (updateObjectsList) {
          updateObjectsList();
        }
      }
    };
  
  // Load initial images when Photos section is selected
  useEffect(() => {
    if (activeSection === 'photos' && photosImages.length === 0) {
      searchPhotos('people photography');
    }
  }, [activeSection]);
  
  // Set larger control sizes for better selection
  useEffect(() => {
    if (fabricCanvasRef.current) {
      // Increase the size of control points
      fabric.Object.prototype.cornerSize = 12;
      fabric.Object.prototype.transparentCorners = false;
      fabric.Object.prototype.cornerColor = '#4890fe';
      fabric.Object.prototype.cornerStrokeColor = '#ffffff';
      fabric.Object.prototype.borderColor = '#4890fe';
      fabric.Object.prototype.borderScaleFactor = 2;
      fabric.Object.prototype.borderOpacityWhenMoving = 0.9;
      
      // Increase padding around objects for easier selection
      fabric.Object.prototype.padding = 8;
      
      // Make rotation control more visible
      fabric.Object.prototype.rotatingPointOffset = 30;
      
      // Apply changes to all existing objects
      fabricCanvasRef.current.getObjects().forEach(obj => {
        obj.setCoords();
      });
      
      fabricCanvasRef.current.renderAll();
    }
  }, []); // Remove direct reference to fabricCanvasRef.current

  // Function to create pattern canvas
  const createPatternCanvas = (pattern, bgColor, fgColor, size = 20) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Increase the size for more spacing
    const patternSize = size * 2;
    canvas.width = patternSize;
    canvas.height = patternSize;
    
    // Fill background
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, patternSize, patternSize);
    
    // Draw pattern
    ctx.fillStyle = fgColor;
    
    switch (pattern) {
      case 'dots':
        ctx.beginPath();
        ctx.arc(patternSize/2, patternSize/2, patternSize/10, 0, Math.PI * 2);
        ctx.fill();
        break;
        
      case 'grid':
        ctx.lineWidth = 1;
        ctx.strokeStyle = fgColor;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(0, patternSize);
        ctx.moveTo(0, 0);
        ctx.lineTo(patternSize, 0);
        ctx.stroke();
        break;
        
      case 'stripes':
        ctx.beginPath();
        ctx.rect(0, 0, patternSize, patternSize/8);
        ctx.fill();
        break;
        
      case 'diagonal':
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(patternSize, patternSize);
        ctx.lineWidth = patternSize/16;
        ctx.strokeStyle = fgColor;
        ctx.stroke();
        break;
        
      case 'checkerboard':
        const quarterSize = patternSize/2;
        ctx.fillRect(0, 0, quarterSize - 2, quarterSize - 2);
        ctx.fillRect(quarterSize + 2, quarterSize + 2, quarterSize - 2, quarterSize - 2);
        break;
        
      case 'zigzag':
        ctx.beginPath();
        ctx.moveTo(0, patternSize/2);
        ctx.lineTo(patternSize/4, patternSize/4);
        ctx.lineTo(patternSize/2, patternSize/2);
        ctx.lineTo(3*patternSize/4, 3*patternSize/4);
        ctx.lineTo(patternSize, patternSize/2);
        ctx.lineWidth = patternSize/16;
        ctx.strokeStyle = fgColor;
        ctx.stroke();
        break;
        
      default:
        ctx.fillRect(0, 0, patternSize, patternSize);
    }
    
    return canvas;
  };

  // Function to apply pattern to a specific slide - simplified approach without fabric.Pattern
  const applyPatternToSlide = (patternInfo, slideIndex) => {
    const canvas = fabricCanvasRef.current;
    const slideWidth = 1080;
    const slideHeight = canvasSize?.height || 1350;
    const pageOffset = slideIndex * slideWidth;
    
    // Remove any existing background objects on this slide
    const objects = canvas.getObjects();
    const existingBgs = objects.filter(obj => 
      obj.objectType === 'background' && 
      (obj.pageId === slideIndex || obj.pageId === String(slideIndex))
    );
    
    existingBgs.forEach(bg => canvas.remove(bg));
    
    // Create a unique ID for the pattern object
    const patternId = `pattern_${patternInfo.pattern}_${slideIndex}_${Date.now()}`;
    
    // Create a solid color rectangle as background
    const backgroundRect = new fabric.Rect({
      left: pageOffset,
      top: 0,
      width: slideWidth,
      height: slideHeight,
      fill: patternInfo.background,
      selectable: true,
      hasControls: true,
      hasBorders: true,
      objectType: 'background',
      pageId: String(slideIndex),
      name: `Background (Slide ${slideIndex + 1})`,
      id: `${patternId}_bg`
    });
    
    // Add the background rectangle
    canvas.add(backgroundRect);
    
    // Create pattern elements based on the pattern type
    const patternElements = [];
    const patternSize = 40; // Size of each pattern element
    const patternSpacing = 60; // Spacing between pattern elements
    
    // Calculate how many pattern elements we need
    const numCols = Math.ceil(slideWidth / patternSpacing) + 1;
    const numRows = Math.ceil(slideHeight / patternSpacing) + 1;
    
    for (let row = 0; row < numRows; row++) {
      for (let col = 0; col < numCols; col++) {
        let patternElement;
        const x = pageOffset + col * patternSpacing;
        const y = row * patternSpacing;
        
        switch (patternInfo.pattern) {
          case 'dots':
            patternElement = new fabric.Circle({
              left: x,
              top: y,
              radius: patternSize / 6,
              fill: patternInfo.foreground,
              selectable: false,
              hasControls: false,
              hasBorders: false,
              evented: false,
              objectType: 'pattern_element',
              pageId: String(slideIndex),
              patternParentId: patternId
            });
            break;
            
          case 'grid':
            // For grid, we'll create horizontal and vertical lines
            if (col < numCols - 1) {
              const hLine = new fabric.Line([x, y, x + patternSpacing, y], {
                stroke: patternInfo.foreground,
                strokeWidth: 1,
                selectable: false,
                hasControls: false,
                hasBorders: false,
                evented: false,
                objectType: 'pattern_element',
                pageId: String(slideIndex),
                patternParentId: patternId
              });
              patternElements.push(hLine);
            }
            
            if (row < numRows - 1) {
              const vLine = new fabric.Line([x, y, x, y + patternSpacing], {
                stroke: patternInfo.foreground,
                strokeWidth: 1,
                selectable: false,
                hasControls: false,
                hasBorders: false,
                evented: false,
                objectType: 'pattern_element',
                pageId: String(slideIndex),
                patternParentId: patternId
              });
              patternElements.push(vLine);
            }
            continue; // Skip adding the patternElement since we added lines directly
            
          case 'stripes':
            if (row % 2 === 0) {
              patternElement = new fabric.Rect({
                left: x,
                top: y,
                width: patternSpacing,
                height: patternSize / 4,
                fill: patternInfo.foreground,
                selectable: false,
                hasControls: false,
                hasBorders: false,
                evented: false,
                objectType: 'pattern_element',
                pageId: String(slideIndex),
                patternParentId: patternId
              });
            }
            break;
            
          case 'diagonal':
            patternElement = new fabric.Line([x, y, x + patternSize, y + patternSize], {
              stroke: patternInfo.foreground,
              strokeWidth: patternSize / 8,
              selectable: false,
              hasControls: false,
              hasBorders: false,
              evented: false,
              objectType: 'pattern_element',
              pageId: String(slideIndex),
              patternParentId: patternId
            });
            break;
            
          case 'checkerboard':
            if ((row + col) % 2 === 0) {
              patternElement = new fabric.Rect({
                left: x,
                top: y,
                width: patternSize,
                height: patternSize,
                fill: patternInfo.foreground,
                selectable: false,
                hasControls: false,
                hasBorders: false,
                evented: false,
                objectType: 'pattern_element',
                pageId: String(slideIndex),
                patternParentId: patternId
              });
            }
            break;
            
          case 'zigzag':
            // For zigzag, create a polyline
            if (col < numCols - 1) {
              const points = [
                { x: x, y: y + patternSize / 2 },
                { x: x + patternSize / 4, y: y },
                { x: x + patternSize / 2, y: y + patternSize / 2 },
                { x: x + 3 * patternSize / 4, y: y },
                { x: x + patternSize, y: y + patternSize / 2 }
              ];
              
              patternElement = new fabric.Polyline(points, {
                stroke: patternInfo.foreground,
                strokeWidth: patternSize / 8,
                fill: 'transparent',
                selectable: false,
                hasControls: false,
                hasBorders: false,
                evented: false,
                objectType: 'pattern_element',
                pageId: String(slideIndex),
                patternParentId: patternId
              });
            }
            break;
            
          default:
            // Default to a simple square
            patternElement = new fabric.Rect({
              left: x,
              top: y,
              width: patternSize / 2,
              height: patternSize / 2,
              fill: patternInfo.foreground,
              selectable: false,
              hasControls: false,
              hasBorders: false,
              evented: false,
              objectType: 'pattern_element',
              pageId: String(slideIndex),
              patternParentId: patternId
            });
        }
        
        if (patternElement) {
          patternElements.push(patternElement);
        }
      }
    }
    
    // Create a group for all pattern elements
    const patternGroup = new fabric.Group(patternElements, {
      left: pageOffset,
      top: 0,
      width: slideWidth,
      height: slideHeight,
      selectable: true,
      hasControls: true,
      hasBorders: true,
      objectType: 'pattern_group',
      pageId: String(slideIndex),
      name: `Pattern ${patternInfo.pattern} (Slide ${slideIndex + 1})`,
      id: patternId,
      type: 'group',
      patternInfo: {
        ...patternInfo,
        isPattern: true,
        slideIndex: slideIndex
      }
    });
    
    // Add the pattern group
    canvas.add(patternGroup);
    
    // Send background to back, then pattern group in front of it
    backgroundRect.sendToBack();
    patternGroup.bringForward(false);
    
    canvas.renderAll();
    
    // Force update of the objects list to ensure the pattern shows in layers
    setTimeout(() => {
      if (updateObjectsList) {
        updateObjectsList();
      }
    }, 100);
  };

  // Function to apply pattern to all slides
  const applyPatternToAllSlides = (patternInfo) => {
    const totalSlides = pages?.length || 1;
    
    // Apply pattern to each slide sequentially with a small delay
    // to prevent overwhelming the browser with too many pattern creations at once
    let slideIndex = 0;
    
    const applyToNextSlide = () => {
      if (slideIndex < totalSlides) {
        applyPatternToSlide(patternInfo, slideIndex);
        slideIndex++;
        setTimeout(applyToNextSlide, 100); // Slightly longer delay between slides for stability
      }
    };
    
    // Start applying patterns
    applyToNextSlide();
    
    setSnackbar({
      open: true,
      message: `Pattern applied to all ${totalSlides} slides`,
      severity: 'success'
    });
  };

  // Add this function to handle grouped element clicks
  const handleAddGroupedElement = (elementId) => {
    // Call the addGroupedElement function passed as a prop
    addGroupedElement(elementId);
  };

  const renderLeftPanelContent = () => {
    switch (activeSection) {
      case 'elements':
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Elements</Typography>
            
            {/* Basic Elements Category */}
            <Paper
              elevation={0}
              sx={{
                mb: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden'
              }}
            >
              <Box
                onClick={() => setActiveElementTab(activeElementTab === 0 ? null : 0)}
                sx={{
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  bgcolor: activeElementTab === 0 ? 'rgba(72, 144, 254, 0.08)' : 'background.paper',
                  borderBottom: activeElementTab === 0 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.03)'
                  }
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {React.cloneElement(ELEMENT_TABS[0].icon, { sx: { fontSize: 20, color: 'text.secondary' } })}
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {ELEMENT_TABS[0].label}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {/* Preview of elements */}
                  <Box sx={{ display: 'flex', mr: 1 }}>
                    <HeadingIcon sx={{ fontSize: 16, color: 'text.secondary', opacity: 0.7, mr: 0.5 }} />
                    <ParagraphIcon sx={{ fontSize: 16, color: 'text.secondary', opacity: 0.7, mr: 0.5 }} />
                    <ImageIcon sx={{ fontSize: 16, color: 'text.secondary', opacity: 0.7 }} />
                  </Box>
                  {activeElementTab === 0 ? (
                    <ExpandLessIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  )}
                </Box>
              </Box>
            
              {/* Basic Elements Content */}
            {activeElementTab === 0 && (
                <Box sx={{ p: 1.5 }}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <ElementButton onClick={addHeading}>
                    <HeadingIcon sx={{ fontSize: 24, mb: 0.5 }} />
                    <Typography variant="caption">Heading</Typography>
                  </ElementButton>
                </Grid>
                <Grid item xs={6}>
                  <ElementButton onClick={addParagraph}>
                    <ParagraphIcon sx={{ fontSize: 24, mb: 0.5 }} />
                    <Typography variant="caption">Paragraph</Typography>
                  </ElementButton>
                </Grid>
                <Grid item xs={6}>
                  <ElementButton onClick={addImage}>
                    <ImageIcon sx={{ fontSize: 24, mb: 0.5 }} />
                    <Typography variant="caption">Upload Image</Typography>
                  </ElementButton>
                </Grid>
                <Grid item xs={6}>
                  <ElementButton onClick={addProfilePlaceholder}>
                    <AccountCircleIcon sx={{ fontSize: 24, mb: 0.5 }} />
                    <Typography variant="caption">Profile Placeholder</Typography>
                  </ElementButton>
                </Grid>
                <Grid item xs={6}>
                  <ElementButton onClick={addSquareProfilePlaceholder}>
                    <PersonIcon sx={{ fontSize: 24, mb: 0.5 }} />
                    <Typography variant="caption">Square Profile</Typography>
                  </ElementButton>
                </Grid>
                <Grid item xs={6}>
                  <ElementButton onClick={addLogoPlaceholder}>
                    <CropOriginalIcon sx={{ fontSize: 24, mb: 0.5 }} />
                    <Typography variant="caption">Logo Placeholder</Typography>
                  </ElementButton>
                </Grid>
              </Grid>
                </Box>
              )}
            </Paper>
            
            {/* Grouped Elements Category */}
            <Paper
              elevation={0}
              sx={{
                mb: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden'
              }}
            >
              <Box
                onClick={() => setActiveElementTab(activeElementTab === 1 ? null : 1)}
                sx={{
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  bgcolor: activeElementTab === 1 ? 'rgba(72, 144, 254, 0.08)' : 'background.paper',
                  borderBottom: activeElementTab === 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.03)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {React.cloneElement(ELEMENT_TABS[1].icon, { sx: { fontSize: 20, color: 'text.secondary' } })}
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {ELEMENT_TABS[1].label}
                </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {/* Preview of grouped elements */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    mr: 1,
                    border: '1px solid #e0e0e0',
                    borderRadius: '50%',
                    height: 16,
                    width: 16,
                    justifyContent: 'center',
                    overflow: 'hidden',
                    bgcolor: '#f8fafc'
                  }}>
                    <PersonIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
                  </Box>
                  {activeElementTab === 1 ? (
                    <ExpandLessIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  )}
                </Box>
              </Box>
              
              {/* Grouped Elements Content */}
              {activeElementTab === 1 && (
                <Box sx={{ p: 1.5 }}>
                <Grid container spacing={1.5}>
                  {GROUPED_ELEMENTS.map((element) => (
                    <Grid item xs={12} key={element.id}>
                        <GroupedElementCard onClick={() => handleAddGroupedElement(element.id)}>
                        <PreviewContainer>
                          {element.preview}
                        </PreviewContainer>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <ElementTitle>
                            {element.title}
                          </ElementTitle>
                          <ElementDescription>
                            {element.description}
                          </ElementDescription>
                        </CardContent>
                      </GroupedElementCard>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
            </Paper>
            
            {/* Shapes Category */}
            <Paper
              elevation={0}
              sx={{
                mb: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden'
              }}
            >
              <Box
                onClick={() => setActiveElementTab(activeElementTab === 2 ? null : 2)}
                sx={{
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  bgcolor: activeElementTab === 2 ? 'rgba(72, 144, 254, 0.08)' : 'background.paper',
                  borderBottom: activeElementTab === 2 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.03)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {React.cloneElement(ELEMENT_TABS[2].icon, { sx: { fontSize: 20, color: 'text.secondary' } })}
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {ELEMENT_TABS[2].label}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {/* Preview of shapes */}
                  <Box sx={{ display: 'flex', mr: 1 }}>
                    <Box sx={{ width: 12, height: 12, bgcolor: 'text.disabled', borderRadius: 0.5, mr: 0.5 }} />
                    <Box sx={{ width: 12, height: 12, bgcolor: 'text.disabled', borderRadius: '50%', mr: 0.5 }} />
                    <Box sx={{ 
                      width: 0,
                      height: 0,
                      borderLeft: '6px solid transparent',
                      borderRight: '6px solid transparent',
                      borderBottom: '12px solid',
                      borderBottomColor: 'text.disabled'
                    }} />
                  </Box>
                  {activeElementTab === 2 ? (
                    <ExpandLessIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  )}
                </Box>
              </Box>
              
              {/* Shapes Content */}
            {activeElementTab === 2 && (
                <Box sx={{ p: 1.5 }}>
              <Grid container spacing={1}>
                {SHAPES.map((shape) => (
                  <Grid item xs={4} key={shape.id}>
                    <ElementButton onClick={() => addShape(shape.id)}>
                      {shape.icon}
                      <Typography variant="caption">{shape.name}</Typography>
                    </ElementButton>
                  </Grid>
                ))}
              </Grid>
                </Box>
            )}
            </Paper>
            
            {/* Emojis Category */}
            <Paper
              elevation={0}
              sx={{
                mb: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden'
              }}
            >
              <Box
                onClick={() => setActiveElementTab(activeElementTab === 3 ? null : 3)}
                sx={{
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  bgcolor: activeElementTab === 3 ? 'rgba(72, 144, 254, 0.08)' : 'background.paper',
                  borderBottom: activeElementTab === 3 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.03)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {React.cloneElement(ELEMENT_TABS[3].icon, { sx: { fontSize: 20, color: 'text.secondary' } })}
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {ELEMENT_TABS[3].label}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {/* Preview of emojis */}
                  <Box sx={{ display: 'flex', mr: 1 }}>
                    <Typography sx={{ fontSize: 14, mr: 0.5 }}>😀</Typography>
                    <Typography sx={{ fontSize: 14, mr: 0.5 }}>🎉</Typography>
                    <Typography sx={{ fontSize: 14 }}>👍</Typography>
                  </Box>
                  {activeElementTab === 3 ? (
                    <ExpandLessIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  )}
                </Box>
              </Box>
              
              {/* Emojis Content */}
            {activeElementTab === 3 && (
                <Box sx={{ p: 1.5 }}>
                {EMOJI_CATEGORIES.map((category) => (
                  <Box key={category.name} sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#64748b' }}>
                      {category.name}
                    </Typography>
                    <Grid container spacing={0.5}>
                      {category.emojis.slice(0, 24).map((emoji) => (
                        <Grid item key={emoji}>
                          <Button
                            onClick={() => addEmoji(emoji)}
                            sx={{
                              minWidth: 32,
                              width: 32,
                              height: 32,
                              p: 0,
                              fontSize: '1.2rem',
                              '&:hover': { bgcolor: 'rgba(72, 144, 254, 0.08)' }
                            }}
                          >
                            {emoji}
                          </Button>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ))}
              </Box>
            )}
            </Paper>
          </Box>
        );

      case 'text':
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Text</Typography>
            <Stack spacing={1}>
              <ModernButton startIcon={<HeadingIcon />} onClick={addHeading} fullWidth>
                Add Heading
              </ModernButton>
              <ModernButton startIcon={<ParagraphIcon />} onClick={addParagraph} fullWidth>
                Add Paragraph
              </ModernButton>
            </Stack>
          </Box>
        );

      case 'photos':
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Photos</Typography>
            <Stack spacing={2}>
              <ModernButton startIcon={<UploadIcon />} onClick={addImage} fullWidth>
                Upload Image
              </ModernButton>
              
              {/* Stock Photos Section */}
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#64748b', mb: 1 }}>
                Stock Photos
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <form onSubmit={handlePhotosSearch}>
                  <TextField
                fullWidth
                    placeholder="Search for people, objects, landscapes..."
                    value={photosSearchQuery}
                    onChange={(e) => setPhotosSearchQuery(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton type="submit" edge="end">
                            <SearchIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                sx={{ 
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </form>
              </Box>
              
              {/* Category chips for Photos */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {['People', 'Business', 'Office', 'Portrait', 'Lifestyle', 'Nature', 'Travel', 'Food'].map((category) => (
                  <Chip 
                    key={category}
                    label={category}
                    onClick={() => handlePhotosCategoryClick(category)}
                    sx={{ 
                      borderRadius: 1,
                      backgroundColor: photosSearchQuery === category ? 'rgba(72, 144, 254, 0.1)' : 'transparent',
                      border: photosSearchQuery === category ? '1px solid #4890fe' : '1px solid #e2e8f0',
                      color: photosSearchQuery === category ? '#4890fe' : '#64748b',
                  '&:hover': { 
                        backgroundColor: 'rgba(72, 144, 254, 0.05)',
                    borderColor: '#4890fe'
                  }
                }}
                  />
                ))}
              </Box>
              
              {/* Loading state */}
              {photosLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={40} sx={{ color: '#4890fe' }} />
                </Box>
              )}
              
              {/* Error state */}
              {photosError && (
                <Typography color="error" align="center" sx={{ py: 2 }}>
                  {photosError}
              </Typography>
              )}
              
              {/* Images grid */}
              {!photosLoading && photosImages.length > 0 && (
                <>
                  <ImageList cols={2} gap={8} sx={{ mb: 2 }}>
                    {photosImages.map((image) => (
                      <ImageListItem 
                        key={image.id}
                onClick={() => {
                          // Add image to canvas
                          if (fabricCanvasRef.current) {
                            fabric.Image.fromURL(image.src.large, (img) => {
                              // Calculate position - center in current page
                              const pageWidth = 1080;
                              const pageOffset = currentPageIndex * pageWidth;
                              const canvasCenter = pageOffset + pageWidth / 2;
                              
                              // Scale the image to a reasonable size
                              const maxDimension = 400;
                              let scale = 1;
                              
                              if (img.width > img.height) {
                                scale = maxDimension / img.width;
                  } else {
                                scale = maxDimension / img.height;
                              }
                              
                              img.set({
                                left: canvasCenter,
                                top: (canvasSize?.height || 1350) / 2,
                                originX: 'center',
                                originY: 'center',
                                scaleX: scale,
                                scaleY: scale,
                                id: `pexels_image_${Date.now()}`
                              });
                              
                              fabricCanvasRef.current.add(img);
                              fabricCanvasRef.current.setActiveObject(img);
                              fabricCanvasRef.current.renderAll();
                              
                              // Update objects list if needed
                              if (updateObjectsList) {
                                updateObjectsList();
                              }
                              
                    setSnackbar({ 
                      open: true, 
                                message: 'Stock image added to canvas',
                                severity: 'success'
                              });
                    });
                  }
                }}
                sx={{ 
                          cursor: 'pointer',
                          borderRadius: 1,
                          overflow: 'hidden',
                  '&:hover': { 
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            transform: 'translateY(-2px)',
                            transition: 'all 0.2s ease'
                          }
                        }}
                      >
                        <img
                          src={image.src.medium}
                          alt={image.photographer}
                          loading="lazy"
                          style={{ 
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            aspectRatio: '1/1'
                          }}
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                  
                  {/* Pagination */}
                  {photosTotalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Pagination 
                        count={photosTotalPages > 10 ? 10 : photosTotalPages} 
                        page={photosPage}
                        onChange={handlePhotosPageChange}
                        size="small"
                        sx={{
                          '& .MuiPaginationItem-root.Mui-selected': {
                            backgroundColor: '#4890fe',
                            color: 'white'
                          }
                        }}
                      />
                    </Box>
                  )}
                </>
              )}
              
              {/* Empty state */}
              {!photosLoading && photosImages.length === 0 && !photosError && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <PhotosIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 2 }} />
                  <Typography variant="body2" color="textSecondary">
                    No images found. Try a different search term.
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>
        );

      case 'ai-image':
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>AI Image Generator</Typography>
            
            <TextField
              fullWidth
              multiline
              rows={4}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Be descriptive about the image you want to generate..."
              sx={{ 
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#4890fe',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#4890fe',
                  }
                }
              }}
            />
            
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#4A5568' }}>
              Image Size
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <Select
                value={imageSize}
                onChange={(e) => setImageSize(e.target.value)}
                sx={{ 
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.1)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4890fe',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4890fe',
                  }
                }}
              >
                <MenuItem value="square_hd">Square HD</MenuItem>
                <MenuItem value="portrait_4_3">Portrait</MenuItem>
                <MenuItem value="landscape_4_3">Landscape</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel sx={{ fontWeight: 500 }}>Image Style</InputLabel>
              <Select
                value={imageStyle}
                onChange={handleStyleChange}
                label="Image Style"
                sx={{ 
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.1)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4890fe',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4890fe',
                  }
                }}
              >
                <MenuItem value="">None</MenuItem>
                {IMAGE_STYLES.map(style => (
                  <MenuItem 
                    key={style.value} 
                    value={style.value}
                    sx={{ 
                      padding: '12px 16px',
                    }}
                  >
                    {style.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
                fullWidth
              variant="contained"
              onClick={handleGenerateImage}
              disabled={loading || !prompt}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <GenerateIcon />}
                sx={{ 
                height: 48,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '16px',
                bgcolor: '#4890fe',
                boxShadow: 'none',
                mb: 2,
                  '&:hover': { 
                  bgcolor: '#3a7ad9',
                  boxShadow: 'none'
                  },
                  '&:disabled': {
                  bgcolor: '#e0e0e0'
                }
              }}
            >
              {loading ? 'Generating...' : (
                <>
                  Generate Image
                  <Typography component="span" sx={{
                    ml: 1,
                    fontSize: '0.75rem',
                    color: !prompt ? 'rgba(255, 255, 255, 0.5)' : 'white'
                  }}>
                    (1 credit)
                  </Typography>
                </>
              )}
            </Button>
            
            {generatedImage && (
              <>
                <Box
                  sx={{
                    mt: 2,
                    mb: 3,
                    display: 'flex',
                    justifyContent: 'center',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    borderRadius: 2,
                    p: 1,
                    position: 'relative',
                    overflow: 'hidden',
                    height: 200,
                }}
              >
                  <img 
                    src={generatedImage} 
                    alt="Generated artwork"
                    style={{ 
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                    }} 
                  />
                </Box>
                
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleAddGeneratedImage}
                  disabled={loading}
                  startIcon={<Add />}
                  sx={{ 
                    height: 48,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '16px',
                    borderColor: '#4890fe',
                    color: '#4890fe',
                    '&:hover': {
                      borderColor: '#3a7ad9',
                      bgcolor: 'rgba(72, 144, 254, 0.04)'
                    }
                  }}
                >
                  Add to Canvas
                </Button>
              </>
            )}
          </Box>
        );

      case 'background':
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Background</Typography>
            
            {/* Background Mode Selection */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
                Apply Background To:
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant={backgroundMode === 'all' ? 'contained' : 'outlined'}
                    onClick={() => setBackgroundMode('all')}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      bgcolor: backgroundMode === 'all' ? '#4890fe' : 'transparent',
                      borderColor: backgroundMode === 'all' ? '#4890fe' : 'rgba(0, 0, 0, 0.12)',
                      color: backgroundMode === 'all' ? 'white' : '#4A5568',
                      '&:hover': {
                        bgcolor: backgroundMode === 'all' ? '#3a7ad9' : 'rgba(0, 0, 0, 0.04)',
                      }
                    }}
                  >
                    All Slides
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant={backgroundMode === 'perSlide' ? 'contained' : 'outlined'}
                    onClick={() => setBackgroundMode('perSlide')}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      bgcolor: backgroundMode === 'perSlide' ? '#4890fe' : 'transparent',
                      borderColor: backgroundMode === 'perSlide' ? '#4890fe' : 'rgba(0, 0, 0, 0.12)',
                      color: backgroundMode === 'perSlide' ? 'white' : '#4A5568',
                      '&:hover': {
                        bgcolor: backgroundMode === 'perSlide' ? '#3a7ad9' : 'rgba(0, 0, 0, 0.04)',
                      }
                    }}
                  >
                    Per Slide
                  </Button>
                </Grid>
              </Grid>
            </Box>
            
            {/* Slide Selection (visible only in perSlide mode) */}
            {backgroundMode === 'perSlide' && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#1e293b' }}>
                  Select Slide:
                </Typography>
                <Grid container spacing={1}>
                  {Array.from({ length: pages?.length || 1 }, (_, i) => (
                    <Grid item xs={3} key={i}>
                      <Button
                        fullWidth
                        variant={selectedSlideForBackground === i ? 'contained' : 'outlined'}
                        onClick={() => setSelectedSlideForBackground(i)}
                        sx={{
                          minWidth: 0,
                          height: 40,
                          p: 0,
                          borderRadius: 2,
                          bgcolor: selectedSlideForBackground === i ? '#4890fe' : 'transparent',
                          borderColor: selectedSlideForBackground === i ? '#4890fe' : 'rgba(0, 0, 0, 0.12)',
                          color: selectedSlideForBackground === i ? 'white' : '#4A5568',
                          '&:hover': {
                            bgcolor: selectedSlideForBackground === i ? '#3a7ad9' : 'rgba(0, 0, 0, 0.04)',
                          }
                        }}
                      >
                        {i + 1}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
            
            <Tabs 
              value={activeBackgroundTab} 
              onChange={(e, newValue) => setActiveBackgroundTab(newValue)}
              variant="fullWidth"
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Colors & Gradients" sx={{ textTransform: 'none', fontSize: '0.75rem', minHeight: 48 }} />
              <Tab label="Stock Images" sx={{ textTransform: 'none', fontSize: '0.75rem', minHeight: 48 }} />
              <Tab label="Patterns" sx={{ textTransform: 'none', fontSize: '0.75rem', minHeight: 48 }} />
            </Tabs>
            
            {/* Colors & Gradients Tab */}
            {activeBackgroundTab === 0 && (
              <Box>
                {/* Solid Colors Section */}
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#1e293b' }}>
                  Preset Colors
                </Typography>
                <Grid container spacing={1} sx={{ mb: 3 }}>
                  {presetColors.slice(0, 18).map((color) => (
                    <Grid item xs={2} key={color}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: color,
                          border: background === color ? '3px solid #4890fe' : '2px solid #e2e8f0',
                          borderRadius: 1,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': { transform: 'scale(1.1)' }
                        }}
                        onClick={() => {
                          setBackground(color);
                          
                          if (backgroundMode === 'all') {
                            // Apply to all slides - set canvas background color
                          fabricCanvasRef.current.setBackgroundColor(color, fabricCanvasRef.current.renderAll.bind(fabricCanvasRef.current));
                          } else {
                            // Apply to selected slide only - create a background rectangle
                            const canvas = fabricCanvasRef.current;
                            const slideWidth = 1080;
                            // Get the actual canvas height from the canvasSize prop
                            const slideHeight = canvasSize?.height || 1350; // Default to 1350 if canvasSize is not available
                            const pageOffset = selectedSlideForBackground * slideWidth;
                            
                            // Remove any existing background objects on this slide
                            const objects = canvas.getObjects();
                            const existingBgs = objects.filter(obj => 
                              obj.objectType === 'background' && 
                              (obj.pageId === selectedSlideForBackground || obj.pageId === selectedSlideForBackground.toString())
                            );
                            
                            existingBgs.forEach(bg => canvas.remove(bg));
                            
                            // Create a new background rectangle
                            const rect = new fabric.Rect({
                              left: pageOffset,
                              top: 0,
                              width: slideWidth,
                              height: slideHeight,
                              fill: String(color),
                              selectable: true,
                              hasControls: true,
                              hasBorders: true,
                              lockMovementX: false,
                              lockMovementY: false,
                              lockRotation: false,
                              lockScalingX: false,
                              lockScalingY: false,
                              evented: true,
                              objectType: 'background',
                              pageId: String(selectedSlideForBackground), // Convert to string to ensure consistent type
                              name: `Background Color (Slide ${selectedSlideForBackground + 1})`
                            });
                            
                            // Add the new background and send it to the back
                            canvas.add(rect);
                            rect.sendToBack();
                            canvas.renderAll();
                            
                            // Update objects list if needed
                            if (updateObjectsList) {
                              updateObjectsList();
                            }
                            
                            setSnackbar({
                              open: true,
                              message: `Background color applied to slide ${selectedSlideForBackground + 1}`,
                              severity: 'success'
                            });
                          }
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
            
                {/* Gradients Section */}
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#1e293b' }}>
                  Gradient Presets
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {GRADIENT_PRESETS.map((gradient) => (
                    <Grid item xs={6} key={gradient.name}>
                      <Box
                        sx={{
                          height: 60,
                          background: gradient.gradient,
                          borderRadius: 2,
                          cursor: 'pointer',
                          border: '2px solid transparent',
                          transition: 'all 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          '&:hover': { 
                            transform: 'scale(1.02)',
                            border: '2px solid #4890fe'
                          }
                        }}
                        onClick={() => {
                          if (backgroundMode === 'all') {
                            // Use the new function to apply gradient to all slides
                            applyGradientToAllSlides({
                              ...gradient,
                              // Ensure we pass proper string values to avoid type comparison issues
                              colors: gradient.colors.map(color => String(color))
                            });
                          } else {
                            // Apply gradient to the selected slide only
                            applyGradientToSlide({
                              ...gradient,
                              // Ensure we pass proper string values to avoid type comparison issues
                              colors: gradient.colors.map(color => String(color))
                            }, selectedSlideForBackground);
                            setSnackbar({
                              open: true,
                              message: `${gradient.name} gradient applied to slide ${selectedSlideForBackground + 1}`,
                              severity: 'success'
                            });
                          }
                        }}
                      >
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'white',
                            fontWeight: 'bold',
                            textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                            textAlign: 'center'
                          }}
                        >
                          {gradient.name}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
            
                {/* Custom Color Picker Section */}
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#1e293b' }}>
                  Custom Color
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <HexColorPicker
                    color={background}
                    onChange={(color) => {
                      setBackground(color);
                      
                      if (backgroundMode === 'all') {
                        // Apply to all slides - set canvas background color
                      fabricCanvasRef.current.setBackgroundColor(String(color), fabricCanvasRef.current.renderAll.bind(fabricCanvasRef.current));
                      } else {
                        // Apply to selected slide only - create a background rectangle
                        const canvas = fabricCanvasRef.current;
                        const slideWidth = 1080;
                        // Get the actual canvas height from the canvasSize prop
                        const slideHeight = canvasSize?.height || 1350; // Default to 1350 if canvasSize is not available
                        const pageOffset = selectedSlideForBackground * slideWidth;
                        
                        // Remove any existing background objects on this slide
                        const objects = canvas.getObjects();
                        const existingBgs = objects.filter(obj => 
                          obj.objectType === 'background' && 
                          (obj.pageId === selectedSlideForBackground || obj.pageId === selectedSlideForBackground.toString())
                        );
                        
                        existingBgs.forEach(bg => canvas.remove(bg));
                        
                        // Create a new background rectangle
                        const rect = new fabric.Rect({
                          left: pageOffset,
                          top: 0,
                          width: slideWidth,
                          height: slideHeight,
                          fill: String(color),
                          selectable: true,
                          hasControls: true,
                          hasBorders: true,
                          lockMovementX: false,
                          lockMovementY: false,
                          lockRotation: false,
                          lockScalingX: false,
                          lockScalingY: false,
                          evented: true,
                          objectType: 'background',
                          pageId: String(selectedSlideForBackground), // Convert to string to ensure consistent type
                          name: `Background Color (Slide ${selectedSlideForBackground + 1})`
                        });
                        
                        // Add the new background and send it to the back
                        canvas.add(rect);
                        rect.sendToBack();
                        canvas.renderAll();
                        
                        // Update objects list if needed
                        if (updateObjectsList) {
                          updateObjectsList();
                        }
                      }
                    }}
                    style={{ width: '200px', height: '200px' }}
                  />
                </Box>
              </Box>
            )}
            
            {/* Stock Images Tab */}
            {activeBackgroundTab === 1 && (
              <Box>
                <Box sx={{ mb: 2 }}>
                  <form onSubmit={handleBackgroundSearch}>
                    <TextField
                      fullWidth
                      placeholder="Search for background images..."
                      value={backgroundSearchQuery}
                      onChange={(e) => setBackgroundSearchQuery(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton type="submit" edge="end">
                              <SearchIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </form>
                </Box>
                
                {/* Category chips for backgrounds */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {['Abstract', 'Gradient', 'Texture', 'Pattern', 'Minimal', 'Geometric', 'Nature', 'Bokeh', 'Marble', 'Colorful'].map((category) => (
                    <Chip 
                      key={category}
                      label={category}
                      onClick={() => handleBackgroundCategoryClick(category)}
                      sx={{ 
                        borderRadius: 1,
                        backgroundColor: backgroundSearchQuery === category ? 'rgba(72, 144, 254, 0.1)' : 'transparent',
                        border: backgroundSearchQuery === category ? '1px solid #4890fe' : '1px solid #e2e8f0',
                        color: backgroundSearchQuery === category ? '#4890fe' : '#64748b',
                        '&:hover': { 
                          backgroundColor: 'rgba(72, 144, 254, 0.05)',
                          borderColor: '#4890fe'
                        }
                      }}
                    />
                  ))}
                </Box>
                
                {/* Loading state */}
                {backgroundLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={40} sx={{ color: '#4890fe' }} />
                  </Box>
                )}
                
                {/* Error state */}
                {backgroundError && (
                  <Typography color="error" align="center" sx={{ py: 2 }}>
                    {backgroundError}
                  </Typography>
                )}
                
                {/* Images grid */}
                {!backgroundLoading && backgroundImages.length > 0 && (
                  <>
                    <ImageList cols={2} gap={8} sx={{ mb: 2 }}>
                      {backgroundImages.map((image) => (
                        <ImageListItem 
                          key={image.id}
                          onClick={() => handleSelectBackgroundImage(image)}
                          sx={{ 
                            cursor: 'pointer',
                            borderRadius: 1,
                            overflow: 'hidden',
                            '&:hover': { 
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                              transform: 'translateY(-2px)',
                              transition: 'all 0.2s ease'
                            }
                          }}
                        >
                          <img
                            src={image.src.medium}
                            alt={image.photographer}
                            loading="lazy"
                            style={{ 
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              aspectRatio: '1/1'
                            }}
                          />
                        </ImageListItem>
                      ))}
                    </ImageList>
                    
                    {/* Pagination */}
                    {backgroundTotalPages > 1 && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Pagination 
                          count={backgroundTotalPages > 10 ? 10 : backgroundTotalPages} 
                          page={backgroundPage}
                          onChange={handleBackgroundPageChange}
                          size="small"
                          sx={{
                            '& .MuiPaginationItem-root.Mui-selected': {
                              backgroundColor: '#4890fe',
                              color: 'white'
                            }
                          }}
                        />
                      </Box>
                    )}
                  </>
                )}
                
                {/* Empty state */}
                {!backgroundLoading && backgroundImages.length === 0 && !backgroundError && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <PhotosIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 2 }} />
                    <Typography variant="body2" color="textSecondary">
                      No images found. Try a different search term.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            
            {/* Patterns Tab */}
            {activeBackgroundTab === 2 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#1e293b' }}>
                  Pattern Backgrounds
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {PATTERN_PRESETS.map((pattern) => {
                    // Create a small preview canvas
                    const previewCanvas = createPatternCanvas(
                      pattern.pattern,
                      pattern.background,
                      pattern.foreground,
                      10
                    );
                    
                    // Get data URL for the preview
                    const previewUrl = previewCanvas.toDataURL();
                    
                    return (
                      <Grid item xs={6} key={pattern.name}>
                        <Box
                          sx={{
                            height: 60,
                            background: `url(${previewUrl})`,
                            backgroundRepeat: 'repeat',
                            borderRadius: 2,
                            cursor: 'pointer',
                            border: '2px solid transparent',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            '&:hover': { 
                              transform: 'scale(1.02)',
                              border: '2px solid #4890fe'
                            }
                          }}
                          onClick={() => {
                            // Apply the pattern using our updated pattern functions
                            if (backgroundMode === 'all') {
                              applyPatternToAllSlides(pattern);
                            } else {
                              applyPatternToSlide(pattern, selectedSlideForBackground);
                            }
                            
                            setSnackbar({
                              open: true,
                              message: backgroundMode === 'all' 
                                ? `${pattern.name} pattern applied to all slides!` 
                                : `${pattern.name} pattern applied to slide ${selectedSlideForBackground + 1}!`,
                              severity: 'success'
                            });
                          }}
                        >
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#333333',
                              fontWeight: 'bold',
                              backgroundColor: 'rgba(255,255,255,0.7)',
                              padding: '2px 8px',
                              borderRadius: 1,
                              textAlign: 'center'
                            }}
                          >
                            {pattern.name}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
                
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#1e293b' }}>
                  Pattern Colors
                </Typography>
                <Grid container spacing={1} sx={{ mb: 3 }}>
                  {presetColors.slice(0, 6).map((color) => (
                    <Grid item xs={2} key={color}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: color,
                          border: '2px solid #e2e8f0',
                          borderRadius: 1,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': { transform: 'scale(1.1)' }
                        }}
                        onClick={() => {
                          // Find the currently selected pattern and update its foreground color
                          const selectedPattern = {...PATTERN_PRESETS[0]};
                          selectedPattern.foreground = color;
                          
                          if (backgroundMode === 'all') {
                            applyPatternToAllSlides(selectedPattern);
                          } else {
                            applyPatternToSlide(selectedPattern, selectedSlideForBackground);
                            setSnackbar({
                              open: true,
                              message: `Pattern with ${color} applied to slide ${selectedSlideForBackground + 1}`,
                              severity: 'success'
                            });
                          }
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
                
                {/* Pattern Customization */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#1e293b' }}>
                    Customize Pattern
                  </Typography>
                  
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Pattern Type</InputLabel>
                        <Select
                          value={selectedPatternType || 'dots'}
                          onChange={(e) => setSelectedPatternType(e.target.value)}
                          label="Pattern Type"
                        >
                          {PATTERN_PRESETS.map((pattern) => (
                            <MenuItem key={pattern.pattern} value={pattern.pattern}>
                              {pattern.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => {
                          const customPattern = {
                            pattern: selectedPatternType || 'dots',
                            background: patternBgColor || '#ffffff',
                            foreground: patternFgColor || '#e0e0e0',
                            name: 'Custom Pattern'
                          };
                          
                          if (backgroundMode === 'all') {
                            applyPatternToAllSlides(customPattern);
                          } else {
                            applyPatternToSlide(customPattern, selectedSlideForBackground);
                            setSnackbar({
                              open: true,
                              message: `Custom pattern applied to slide ${selectedSlideForBackground + 1}`,
                              severity: 'success'
                            });
                          }
                        }}
                        sx={{
                          height: 40,
                          bgcolor: '#4890fe',
                          '&:hover': { bgcolor: '#3a7ad9' }
                        }}
                      >
                        Apply Custom
                      </Button>
                    </Grid>
                  </Grid>
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Background Color:
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <HexColorPicker
                        color={patternBgColor || '#ffffff'}
                        onChange={(color) => setPatternBgColor(color)}
                        style={{ width: '100%', height: '120px' }}
                      />
                    </Box>
                    
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Pattern Color:
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <HexColorPicker
                        color={patternFgColor || '#e0e0e0'}
                        onChange={(color) => setPatternFgColor(color)}
                        style={{ width: '100%', height: '120px' }}
                      />
                    </Box>
                    
                    {/* Preview */}
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Preview:
                    </Typography>
                    <Box sx={{ 
                      height: 80, 
                      border: '1px solid #e2e8f0',
                      borderRadius: 1,
                      overflow: 'hidden',
                      backgroundImage: patternBgColor && patternFgColor && selectedPatternType ? 
                        `url(${createPatternCanvas(selectedPatternType, patternBgColor, patternFgColor, 15).toDataURL()})` : 
                        'none',
                      backgroundRepeat: 'repeat'
                    }} />
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        );

      case 'branding':
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Branding</Typography>
            
            {/* Active Brand Colors */}
            {selectedBrandAccent && (
              <Box sx={{ mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: '#1e293b' }}>
                  Active Brand Colors - {selectedBrandAccent.name}
                </Typography>
                <Grid container spacing={1}>
                  {['primary', 'secondary', 'accent', 'light'].map((colorType) => (
                    <Grid item xs={3} key={colorType}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: selectedBrandAccent[colorType],
                            borderRadius: 1,
                            mx: 'auto',
                            mb: 1,
                            cursor: 'pointer',
                            border: '2px solid white',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            transition: 'all 0.2s',
                            '&:hover': { transform: 'scale(1.1)' }
                          }}
                        />
                        <Typography variant="caption" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                          {colorType}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
            
            <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
              Professional Color Palettes
            </Typography>
            
            <Stack spacing={2}>
              {BRAND_ACCENTS.map((brand) => (
                <Card 
                  key={brand.name}
                  sx={{ 
                    cursor: 'pointer',
                    border: selectedBrandAccent?.name === brand.name ? '2px solid #4890fe' : '1px solid #e2e8f0',
                    transition: 'all 0.2s',
                    '&:hover': { 
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }
                  }}
                  onClick={() => applyBrandAccent(brand)}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {brand.name}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 24, height: 24, backgroundColor: brand.primary, borderRadius: '50%', border: '2px solid white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }} />
                      <Box sx={{ width: 20, height: 20, backgroundColor: brand.secondary, borderRadius: '50%', border: '2px solid white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }} />
                      <Box sx={{ width: 16, height: 16, backgroundColor: brand.accent, borderRadius: '50%', border: '2px solid white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }} />
                      <Box sx={{ width: 12, height: 12, backgroundColor: brand.light, borderRadius: '50%', border: '1px solid #e2e8f0' }} />
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>
        );

      case 'layers':
        // Group objects by page
        const objectsByPage = {};
        canvasObjects.forEach(obj => {
          const pageId = obj.pageId || obj.pageIndex || 0;
          if (!objectsByPage[pageId]) {
            objectsByPage[pageId] = [];
          }
          objectsByPage[pageId].push(obj);
        });

        // Function to reorder canvas objects
        const reorderCanvasObjects = (sourceIndex, destinationIndex, pageId) => {
          if (sourceIndex === destinationIndex) return;

          const canvas = fabricCanvasRef.current;
          if (!canvas) return;
          
          // Get all objects on this page
          const pageObjects = canvas.getObjects().filter(obj => {
            if (!obj.left) return false;
            const objPageId = Math.floor(obj.left / 1080);
            return objPageId === parseInt(pageId);
          });

          // Reverse the indices since the UI displays them in reverse order
          const actualSourceIndex = pageObjects.length - 1 - sourceIndex;
          const actualDestIndex = pageObjects.length - 1 - destinationIndex;
          
          // Get the objects to reorder
          const objectToMove = pageObjects[actualSourceIndex];
          
          // Change z-index by moving object in canvas
          if (actualSourceIndex < actualDestIndex) {
            // Moving down - place after destination
            canvas.moveTo(objectToMove, canvas.getObjects().indexOf(pageObjects[actualDestIndex]));
          } else {
            // Moving up - place before destination
            canvas.moveTo(objectToMove, canvas.getObjects().indexOf(pageObjects[actualDestIndex]));
          }
          
          // Update the canvas and trigger a modified event to save state
          canvas.renderAll();
          canvas.fire('object:modified'); // This will trigger history saving
          updateObjectsList();
          
          // Show success message
          setSnackbar({
            open: true,
            message: 'Layer order updated',
            severity: 'success'
          });
        };

        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Layers ({canvasObjects.length})
            </Typography>
            
            {Object.keys(objectsByPage).length === 0 ? (
              <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center', py: 4 }}>
                No elements added yet
              </Typography>
            ) : (
              <DragDropContext onDragEnd={(result) => {
                if (!result.destination) return;
                
                const sourcePageId = result.source.droppableId;
                const sourceIndex = result.source.index;
                const destinationIndex = result.destination.index;
                
                reorderCanvasObjects(sourceIndex, destinationIndex, sourcePageId);
              }}>
                <Stack spacing={2}>
                  {Object.entries(objectsByPage).map(([pageId, pageObjects]) => (
                    <Box key={pageId}>
                      {/* Page Header */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1, 
                        mb: 1,
                        p: 1,
                        bgcolor: currentPageIndex === parseInt(pageId) ? 'rgba(72, 144, 254, 0.08)' : 'rgba(0, 0, 0, 0.02)',
                        borderRadius: 1,
                        border: currentPageIndex === parseInt(pageId) ? '1px solid #4890fe' : '1px solid #e2e8f0'
                      }}>
                        <Typography variant="subtitle2" sx={{ 
                          fontWeight: 'bold', 
                          color: currentPageIndex === parseInt(pageId) ? '#4890fe' : '#64748b',
                          flex: 1
                        }}>
                          Slide {parseInt(pageId) + 1} ({pageObjects.length} element{pageObjects.length !== 1 ? 's' : ''})
                        </Typography>
                        {currentPageIndex === parseInt(pageId) && (
                          <Box sx={{ 
                            width: 8, 
                            height: 8, 
                            borderRadius: '50%', 
                            bgcolor: '#4890fe' 
                          }} />
                        )}
                      </Box>
                      
                      {/* Page Objects - Droppable Container */}
                      <Droppable droppableId={pageId}>
                        {(provided) => (
                          <Stack 
                            spacing={1} 
                            sx={{ ml: 1 }}
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                          >
                            {[...pageObjects].reverse().map((obj, index) => {
                              const isLocked = obj.object && (obj.object.lockMovementX || obj.object.lockMovementY || obj.object.selectable === false);
                              const isVisible = obj.object ? (obj.object.visible !== false) : true;
                              
                              return (
                                <Draggable 
                                  key={obj.id || `layer-${pageId}-${index}`} 
                                  draggableId={obj.id || `layer-${pageId}-${index}`}
                                  index={index}
                                  isDragDisabled={isLocked}
                                >
                                  {(provided, snapshot) => (
                                    <Box
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      sx={{
                                        p: 1.5,
                                        borderRadius: 1,
                                        border: snapshot.isDragging ? '1px solid #4890fe' : '1px solid #e2e8f0',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        opacity: isVisible ? 1 : 0.5,
                                        backgroundColor: snapshot.isDragging 
                                          ? 'rgba(72, 144, 254, 0.08)' 
                                          : isLocked 
                                            ? 'rgba(156, 163, 175, 0.05)' 
                                            : 'transparent',
                                        boxShadow: snapshot.isDragging ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
                                        '&:hover': { bgcolor: isLocked ? 'rgba(156, 163, 175, 0.1)' : 'rgba(72, 144, 254, 0.04)' }
                                      }}
                                      onClick={(e) => {
                                        if (e.target.closest('.layer-controls')) return;
                                        if (!isLocked && obj.object) {
                                          fabricCanvasRef.current.setActiveObject(obj.object);
                                          fabricCanvasRef.current.renderAll();
                                          setSelectedObject(obj.object);
                                        }
                                      }}
                                    >
                                      {/* Drag Handle */}
                                      <Box 
                                        {...provided.dragHandleProps}
                                        sx={{ 
                                          color: isLocked ? '#9ca3af' : '#64748b',
                                          cursor: isLocked ? 'not-allowed' : 'grab',
                                          display: 'flex',
                                          alignItems: 'center',
                                          '&:hover': { color: isLocked ? '#9ca3af' : '#4890fe' }
                                        }}
                                      >
                                        <DragHandleIcon fontSize="small" />
                                      </Box>

                                      {/* Element Type Icon */}
                                      <Box sx={{ color: isLocked ? '#9ca3af' : '#64748b' }}>
                                        {obj.type === 'textbox' && <TextIcon fontSize="small" />}
                                        {obj.type === 'rect' && obj.object?.patternInfo?.isPattern && <WallpaperIcon fontSize="small" />}
                                        {obj.type === 'rect' && !obj.object?.patternInfo?.isPattern && <RectangleIcon fontSize="small" />}
                                        {obj.type === 'circle' && <CircleIcon fontSize="small" />}
                                        {obj.type === 'image' && <ImageIcon fontSize="small" />}
                                        {obj.type === 'group' && obj.object?.objectType === 'pattern_group' && <WallpaperIcon fontSize="small" />}
                                        {obj.type === 'group' && obj.object?.objectType !== 'pattern_group' && <LayersIcon fontSize="small" />}
                                        {!['textbox', 'rect', 'circle', 'image', 'group'].includes(obj.type) && <CategoryIcon fontSize="small" />}
                                      </Box>
                                      
                                      {/* Element Name */}
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          fontWeight: 'medium', 
                                          flex: 1,
                                          color: isLocked ? '#9ca3af' : 'inherit',
                                          textDecoration: isLocked ? 'none' : 'none'
                                        }}
                                      >
                                        {obj.name}
                                      </Typography>
                                      
                                      {/* Layer Controls */}
                                      <Box className="layer-controls" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {/* Visibility Toggle */}
                                        <Tooltip title={isVisible ? 'Hide' : 'Show'}>
                                          <Box
                                            sx={{
                                              p: 0.5,
                                              borderRadius: 0.5,
                                              cursor: 'pointer',
                                              color: isVisible ? '#64748b' : '#ef4444',
                                              '&:hover': { 
                                                bgcolor: 'rgba(0, 0, 0, 0.04)',
                                                color: isVisible ? '#374151' : '#dc2626'
                                              }
                                            }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (toggleObjectVisibility && obj.object) {
                                                toggleObjectVisibility(obj.object);
                                              }
                                            }}
                                          >
                                            {isVisible ? (
                                              <VisibilityIcon sx={{ fontSize: 16 }} />
                                            ) : (
                                              <VisibilityOffIcon sx={{ fontSize: 16 }} />
                                            )}
                                          </Box>
                                        </Tooltip>
                                        
                                        {/* Lock Toggle */}
                                        <Tooltip title={isLocked ? 'Unlock' : 'Lock'}>
                                          <Box
                                            sx={{
                                              p: 0.5,
                                              borderRadius: 0.5,
                                              cursor: 'pointer',
                                              color: isLocked ? '#ef4444' : '#64748b',
                                              '&:hover': { 
                                                bgcolor: 'rgba(0, 0, 0, 0.04)',
                                                color: isLocked ? '#dc2626' : '#374151'
                                              }
                                            }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (toggleObjectLock && obj.object) {
                                                toggleObjectLock(obj.object);
                                              }
                                            }}
                                          >
                                            {isLocked ? (
                                              <LockIcon sx={{ fontSize: 16 }} />
                                            ) : (
                                              <LockOpenIcon sx={{ fontSize: 16 }} />
                                            )}
                                          </Box>
                                        </Tooltip>
                                      </Box>
                                    </Box>
                                  )}
                                </Draggable>
                              );
                            })}
                            {provided.placeholder}
                          </Stack>
                        )}
                      </Droppable>
                    </Box>
                  ))}
                </Stack>
              </DragDropContext>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {/* Left Sidebar - Fixed Position */}
      <SidebarContainer sx={{
        position: 'absolute',
        left: 0,
        top: 0,
        zIndex: 1000,
        height: '100%'
      }}>
        {sidebarSections.map((section) => (
          <Tooltip 
            key={section.id}
            title={section.label}
            placement="right"
            arrow
          >
            <SidebarItem
              active={activeSection === section.id}
              onClick={() => handleSectionClick(section.id)}
              sx={{
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateX(2px)',
                }
              }}
            >
              <SidebarIcon active={activeSection === section.id}>
                {React.cloneElement(section.icon, { 
                  sx: { fontSize: 20 } 
                })}
              </SidebarIcon>
              <SidebarLabel active={activeSection === section.id}>
                {section.label}
              </SidebarLabel>
            </SidebarItem>
          </Tooltip>
        ))}
      </SidebarContainer>

      {/* Left Panel Content - Overlay */}
      {activeSection && (
        <Paper sx={{ 
          position: 'absolute',
          left: 80,
          top: 0,
          width: 320, 
          height: '100%', 
          borderRadius: 0, 
          borderRight: '1px solid rgba(0, 0, 0, 0.08)',
          overflow: 'auto',
          zIndex: 999,
          boxShadow: '4px 0 12px rgba(0, 0, 0, 0.15)',
          bgcolor: 'white'
        }}>
          {renderLeftPanelContent()}
        </Paper>
      )}
    </>
  );
};

export default CarouselLeftSidebar; 